import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';

import { Eye, EyeOff } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Input.module.css';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  invalid?: boolean;
  /** Leading icon. Decorative; the label carries the meaning. */
  icon?: ReactNode;
  /** Tabular figures, for IDs, dates and anything compared down a column. */
  mono?: boolean;
  describedBy?: string | undefined;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, icon, mono, describedBy, className, type = 'text', ...rest },
  ref,
) {
  return (
    <div className={styles.wrap}>
      {icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <input
        {...rest}
        ref={ref}
        type={type}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        className={cx(
          styles.input,
          Boolean(icon) && styles.hasIcon,
          mono && styles.mono,
          invalid && styles.invalid,
          className,
        )}
      />
    </div>
  );
});

export type PasswordInputProps = Omit<InputProps, 'type'>;

/**
 * A password field with a reveal toggle.
 *
 * The toggle is a real button so it is reachable by keyboard, and its label changes
 * with its state rather than staying "toggle password".
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ invalid, icon, describedBy, className, ...rest }, ref) {
    const [revealed, setRevealed] = useState(false);

    return (
      <div className={styles.wrap}>
        {icon ? (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <input
          {...rest}
          ref={ref}
          type={revealed ? 'text' : 'password'}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          className={cx(
            styles.input,
            Boolean(icon) && styles.hasIcon,
            styles.hasTrailing,
            invalid && styles.invalid,
            className,
          )}
        />
        <button
          type="button"
          className={styles.trailing}
          onClick={() => {
            setRevealed((value) => !value);
          }}
          aria-label={revealed ? 'Hide password' : 'Show password'}
        >
          {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    );
  },
);
